<template>
  <div
    role="button"
    :tabindex="isClickable ? 0 : -1"
    :aria-disabled="String(!isClickable)"
    :class="[
      'tw-flex tw-w-[328px] tw-flex-shrink tw-flex-col tw-items-start tw-justify-center tw-gap-[16px] tw-rounded-2xl tw-border tw-border-[#E5E7EB] tw-bg-white tw-p-[16px] tw-text-[16px] tw-transition tw-duration-150 tw-ease-in-out md:tw-w-[268px] md:tw-justify-start md:tw-gap-0',
      isClickable
        ? 'tw-cursor-pointer hover:tw-bg-[#F3F4F6] hover:tw-shadow-sm focus:tw-ring-2 focus:tw-ring-[#F48831] focus:tw-ring-opacity-50 active:tw-scale-[0.995] active:tw-bg-[#F9FAFB]'
        : 'tw-cursor-not-allowed',
    ]"
    @click="handleTargetHousemateGroup(groupId)"
  >
    <div
      class="tw-font-inter tw-text-center tw-font-bold tw-leading-[150%] tw-text-[#3B4143]"
    >
      {{ groupData?.housemate_group_name }}
    </div>

    <div
      class="tw-flex tw-flex-row tw-items-center tw-gap-[16px] md:tw-mt-[16px]"
    >
      <div class="tw-flex tw-items-start tw-gap-1">
        <!-- SVG -->
        <HeartStraight />
        <span
          class="tw-font-inter tw-font-bold tw-leading-normal tw-text-[#6B7280]"
        >
          {{ shortlistCount }}
        </span>
        <span
          v-if="newShortlistCount > 0"
          class="tw-font-inter tw-font-normal tw-leading-normal tw-text-[#00A2E1]"
        >
          ({{ newShortlistCount }} new)
        </span>
      </div>

      <div class="tw-flex tw-items-center">
        <!-- SVG -->
        <User />

        <span
          class="tw-font-inter tw-ml-1 tw-font-bold tw-leading-normal tw-text-[#6B7280]"
        >
          {{ membersCount }}
        </span>
      </div>
    </div>

    <!-- Users Pending -->
    <div
      v-if="pendingMembers?.length > 0"
      class="tw-self-stretch tw-border-t-[1px] md:tw-mt-[16px]"
    >
      <div
        class="tw-font-inter tw-flex tw-gap-4 tw-text-sm tw-font-bold tw-leading-normal tw-text-[#6B7280]"
      >
        <div
          :class="{
            'tw-flex-col': otherMembersPendingCount,
            'tw-mr-2': !otherMembersPendingCount,
          }"
          class="tw-font-inter tw-mt-[16px] tw-flex tw-flex-1 tw-items-baseline tw-text-[14px] tw-font-normal tw-leading-normal tw-text-[#6B7280]"
        >
          <span
            >{{ pendingMembersText }}
            {{ otherMembersPendingCount === 0 ? 'pending approval' : '' }}</span
          >

          <span v-if="otherMembersPendingCount > 0">
            and
            <b>
              {{ otherMembersPendingCount }} other<span
                v-if="otherMembersPendingCount > 1"
                >s</span
              >
            </b>
            pending approval
          </span>
        </div>
        <div class="tw-relative tw-mt-[16px]">
          <span class="tw-absolute tw-bottom-[2px] tw-right-0">
            <Eclipse />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import HeartStraight from '@components/Student/SVG/HeartStraight.vue';
import User from '@components/Student/SVG/User.vue';
import Eclipse from '@components/Student/SVG/Eclipse.vue';
import { useStudentStore } from '@js/stores/student/student';

export default {
  name: 'Group',
  components: { Eclipse, User, HeartStraight },
  props: {
    groupData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close-group-list-modal'],
  data() {
    const studentStore = useStudentStore();

    return {
      studentStore,
    };
  },
  computed: {
    groupId() {
      return (
        this.groupData?.housemate_group_id ??
        this.groupData?.housemate_group?.id ??
        null
      );
    },

    permittedGroupIds() {
      return (this.studentStore.getPermittedGroups || []).map((id) =>
        String(id)
      );
    },

    activeGroupId() {
      return String(this.studentStore.targetHousemateGroup?.housemate_group_id);
    },

    isCurrentGroup() {
      return String(this.groupId) === this.activeGroupId;
    },

    isClickable() {
      const id = this.groupId;

      if (!id) {
        return false;
      }

      return (
        this.permittedGroupIds.includes(String(id)) && !this.isCurrentGroup
      );
    },

    shortlistCount() {
      const toFiniteNumber = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const totalCandidates = [
        this.groupData?.property_count,
        this.groupData?.properties_count,
        this.groupData?.total_properties_count,
        this.groupData?.shortlisted_and_enquired_count,
      ];

      for (const value of totalCandidates) {
        const parsed = toFiniteNumber(value);
        if (parsed !== null) {
          return Math.max(0, parsed);
        }
      }

      const shortlistCandidates = [
        this.groupData?.shortlist_count,
        this.groupData?.shortlisted_properties_count,
        this.groupData?.housemate_group_shortlist_count,
      ];

      const enquiredCandidates = [
        this.groupData?.enquired_count,
        this.groupData?.enquired_properties_count,
        this.groupData?.housemate_group_enquired_count,
      ];

      let shortlistCount = null;
      for (const value of shortlistCandidates) {
        const parsed = toFiniteNumber(value);
        if (parsed !== null) {
          shortlistCount = parsed;
          break;
        }
      }

      let enquiredCount = null;
      for (const value of enquiredCandidates) {
        const parsed = toFiniteNumber(value);
        if (parsed !== null) {
          enquiredCount = parsed;
          break;
        }
      }

      if (shortlistCount !== null || enquiredCount !== null) {
        return Math.max(0, (shortlistCount ?? 0) + (enquiredCount ?? 0));
      }

      const fromStore = Number(
        this.studentStore.housemateGroupShortlistedCountByGroupId?.[
          String(this.groupId)
        ]
      );
      if (Number.isFinite(fromStore)) {
        return Math.max(0, fromStore);
      }

      return 0;
    },

    newShortlistCount() {
      const candidates = [
        this.groupData?.new_shortlist_count,
        this.groupData?.new_shortlisted_properties_count,
        this.groupData?.new_properties_count,
      ];

      for (const value of candidates) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
          return Math.max(0, parsed);
        }
      }

      return 0;
    },

    members() {
      return this.groupData?.housemate_group_members ?? [];
    },

    membersCount() {
      return this.members.length;
    },

    pendingMembers() {
      return this.members.filter((member) => member.status === 'PENDING');
    },

    pendingMemberNames() {
      const isSingle = this.pendingMembers.length === 1;
      const currentStudentId = this.studentStore.studentId;

      return this.pendingMembers.map(({ id, name }) => {
        if (id === currentStudentId) {
          return isSingle ? 'Your request is' : 'You';
        }

        return name;
      });
    },

    pendingMembersText() {
      const names = this.pendingMemberNames;

      if (names.length === 0) return '';
      if (names.length === 1) return names[0];
      if (names.length === 2) return `${names[0]} and ${names[1]}`;

      return names.slice(0, 2).join(', ');
    },

    otherMembersPendingCount() {
      return Math.max(this.pendingMemberNames.length - 2, 0);
    },
  },
  methods: {
    handleTargetHousemateGroup(id) {
      if (!this.permittedGroupIds.includes(String(id))) {
        return;
      }

      if (String(id) === this.activeGroupId) {
        return;
      }

      this.$emit('close-group-list-modal');

      if (
        String(id) !==
        String(this.studentStore.targetHousemateGroup?.housemate_group_id)
      ) {
        this.studentStore.postSetDefaultHousemateGroup(id);
      }
    },
  },
};
</script>
